import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ClientDocuments from './ClientDocuments';
import ClientKyc from './ClientKyc';
import ClientQuickActions from './ClientQuickActions';
import ClientTimeline from './ClientTimeline';
import { useParams } from 'react-router-dom';

const ClientDetails = ({ apiBase }) => {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [booking, setBooking] = useState(null);
  const [payments, setPayments] = useState([]);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'NEFT', bank_name: 'HDFC Bank', reference_number: '',
    description: 'Token Amount', notes: '', send_whatsapp: false
  });
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchClientData();
  }, [id]);

  const fetchClientData = async () => {
    try {
      const [clientRes, kycRes, bookingRes, paymentsRes, waRes] = await Promise.all([
        axios.get(`${apiBase}/clients/${id}`),
        Promise.resolve({ data: null }), // old KYC path retired 2026-09-23
        Promise.resolve({ data: null }), // old booking-details removed 2026-09-23
        axios.get(`${apiBase}/payments/client/${id}`).catch(() => ({ data: [] })),
        axios.get(`${apiBase}/client-docs/${id}/whatsapp`).catch(() => ({ data: [] }))
      ]);
      setClient(clientRes.data);
      setKyc(kycRes.data);
      setBooking(bookingRes.data);
      setPayments(paymentsRes.data || []);
      setWhatsappMessages(waRes.data || []);
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || !paymentForm.payment_date) {
      alert('Please fill in amount and date');
      return;
    }
    try {
      await axios.post(`${apiBase}/payments`, {
        client_id: parseInt(id),
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      });
      alert('Payment recorded successfully!' + (paymentForm.send_whatsapp ? ' WhatsApp receipt sent.' : ''));
      setShowPaymentModal(false);
      setPaymentForm({
        amount: '', payment_date: new Date().toISOString().split('T')[0],
        payment_mode: 'NEFT', bank_name: 'HDFC Bank', reference_number: '',
        description: 'Token Amount', notes: '', send_whatsapp: false
      });
      fetchClientData();
    } catch (error) {
      alert('Error recording payment: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_type', documentType);

    try {
      await axios.post(`${apiBase}/kyc/client/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('Document uploaded successfully');
      fetchClientData();
    } catch (error) {
      alert('Error uploading document: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleKYCUpdate = async (data) => {
    try {
      await axios.post(`${apiBase}/kyc/client/${id}`, data);
      alert('KYC updated successfully');
      fetchClientData();
    } catch (error) {
      alert('Error updating KYC: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div className="loading">Loading client details...</div>;
  }

  if (!client) {
    return <div>Client not found</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>{client.name}</h1>
        <p style={{ color: '#64748b' }}>{client.phone} | {client.email}</p>
      </div>

      <ClientQuickActions apiBase={apiBase} clientId={id} clientName={client.name} />
      <ClientTimeline apiBase={apiBase} clientId={id} />

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #e2e8f0' }}>
        <button
          className={`btn ${activeTab === 'details' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('details')}
        >
          Client Details
        </button>
        <button
          className={`btn ${activeTab === 'kyc' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('kyc')}
        >
          Documents & KYC
        </button>
        <button
          className={`btn ${activeTab === 'booking' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('booking')}
        >
          Booking Details
        </button>
        <button
          className={`btn ${activeTab === 'payments' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('payments')}
          style={{ background: activeTab === 'payments' ? 'linear-gradient(135deg, #10b981, #059669)' : undefined }}
        >
          💳 Payments {payments.length > 0 && `(${payments.length})`}
        </button>
        <button
          className={`btn ${activeTab === 'whatsapp' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('whatsapp')}
          style={{ background: activeTab === 'whatsapp' ? '#25D366' : undefined }}
        >
          💬 WhatsApp {whatsappMessages.length > 0 && `(${whatsappMessages.length})`}
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="card">
          <h2 className="card-title">Client Information</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            <div>
              <label className="form-label">Name</label>
              <div className="form-input">{client.name}</div>
            </div>
            <div>
              <label className="form-label">Phone</label>
              <div className="form-input">{client.phone}</div>
            </div>
            <div>
              <label className="form-label">Email</label>
              <div className="form-input">{client.email || '-'}</div>
            </div>
            <div>
              <label className="form-label">Status</label>
              <div className="form-input">
                <span className="badge badge-info">{client.status}</span>
              </div>
            </div>
            <div>
              <label className="form-label">Source</label>
              <div className="form-input">{client.source || '-'}</div>
            </div>
            <div>
              <label className="form-label">Project</label>
              <div className="form-input">{client.project_name || '-'}</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'kyc' && <ClientKyc apiBase={apiBase} clientId={id} />}
      {activeTab === 'kyc' && <ClientDocuments apiBase={apiBase} clientId={id} />}
      {activeTab === 'booking' && (
        <div className="card">
          <h2 className="card-title">Booking Details</h2>
          {!booking && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
              <p>No booking details available.</p>
            </div>
          )}
          {booking && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              <div>
                <label className="form-label">Unit Number</label>
                <div className="form-input">{booking.unit_number}</div>
              </div>
              <div>
                <label className="form-label">Building</label>
                <div className="form-input">{booking.building}</div>
              </div>
              <div>
                <label className="form-label">BHK Type</label>
                <div className="form-input">{booking.bhk_type}</div>
              </div>
              <div>
                <label className="form-label">Booking Date</label>
                <div className="form-input">{booking.booking_date ? new Date(booking.booking_date).toLocaleDateString() : '-'}</div>
              </div>
              <div>
                <label className="form-label">Booking Amount</label>
                <div className="form-input">₹{booking.booking_amount?.toLocaleString() || '0'}</div>
              </div>
              <div>
                <label className="form-label">Total Purchase Price</label>
                <div className="form-input">₹{booking.total_purchase_price?.toLocaleString() || '0'}</div>
              </div>
              <div>
                <label className="form-label">Possession Date</label>
                <div className="form-input">{booking.possession_date ? new Date(booking.possession_date).toLocaleDateString() : '-'}</div>
              </div>
              <div>
                <label className="form-label">Agreement Number</label>
                <div className="form-input">{booking.agreement_number || '-'}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>Payment History</h2>
            <button className="btn btn-primary" onClick={() => setShowPaymentModal(true)} style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
              + Record Payment
            </button>
          </div>

          {payments.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              <p style={{ fontSize: '48px', marginBottom: '16px' }}>💳</p>
              <p>No payments recorded yet.</p>
            </div>
          ) : (
            <>
              <div style={{ background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)', padding: '16px', borderRadius: '12px', marginBottom: '20px' }}>
                <strong style={{ color: '#065f46', fontSize: '18px' }}>
                  Total Received: ₹{payments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString('en-IN')}
                </strong>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Receipt ID</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Mode</th>
                      <th>Bank</th>
                      <th>Reference</th>
                      <th>Description</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => (
                      <tr key={p.id}>
                        <td><strong>PAY-{String(p.id).padStart(5, '0')}</strong></td>
                        <td>{new Date(p.payment_date).toLocaleDateString('en-IN')}</td>
                        <td style={{ fontWeight: '600', color: '#059669' }}>₹{p.amount?.toLocaleString('en-IN')}</td>
                        <td>{p.payment_mode || '-'}</td>
                        <td>{p.bank_name || '-'}</td>
                        <td style={{ fontSize: '12px' }}>{p.reference_number || '-'}</td>
                        <td>{p.description || '-'}</td>
                        <td>
                          <span className={`badge ${p.verified ? 'badge-success' : 'badge-warning'}`}>
                            {p.verified ? '✓ Verified' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* WhatsApp Tab - Chat History */}
      {activeTab === 'whatsapp' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>💬 WhatsApp Chat History</h2>
          </div>

          {whatsappMessages.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              <p style={{ fontSize: '48px', marginBottom: '16px' }}>💬</p>
              <p>No WhatsApp messages found for this client.</p>
              <p style={{ fontSize: '12px', marginTop: '8px' }}>Messages will appear here when WhatsApp communication is logged.</p>
            </div>
          ) : (
            <div style={{ background: '#f0f2f5', borderRadius: '12px', padding: '16px', maxHeight: '500px', overflowY: 'auto' }}>
              {whatsappMessages.map((msg) => (
                <div key={msg.id} style={{
                  display: 'flex', justifyContent: msg.direction === 'incoming' ? 'flex-start' : 'flex-end', marginBottom: '8px'
                }}>
                  <div style={{
                    maxWidth: '70%', padding: '10px 14px', borderRadius: '12px',
                    background: msg.direction === 'incoming' ? 'white' : '#dcf8c6',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ fontSize: '14px', lineHeight: '1.4' }}>{msg.message_content || `[${msg.template_name || 'Media/Template'}]`}</div>
                    <div style={{ fontSize: '10px', color: '#64748b', textAlign: 'right', marginTop: '4px' }}>
                      {new Date(msg.created_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                      {msg.direction === 'outgoing' && (
                        <span style={{ marginLeft: '4px' }}>{msg.status === 'read' ? '✓✓' : msg.status === 'delivered' ? '✓' : '○'}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Stats */}
          {whatsappMessages.length > 0 && (
            <div style={{ display: 'flex', gap: '16px', marginTop: '16px', flexWrap: 'wrap' }}>
              <div style={{ background: '#dcfce7', padding: '12px 20px', borderRadius: '8px' }}>
                <strong style={{ color: '#166534' }}>📥 Incoming: </strong>
                <span>{whatsappMessages.filter(m => m.direction === 'incoming').length}</span>
              </div>
              <div style={{ background: '#dbeafe', padding: '12px 20px', borderRadius: '8px' }}>
                <strong style={{ color: '#1e40af' }}>📤 Outgoing: </strong>
                <span>{whatsappMessages.filter(m => m.direction === 'outgoing').length}</span>
              </div>
              <div style={{ background: '#f5f3ff', padding: '12px 20px', borderRadius: '8px' }}>
                <strong style={{ color: '#7c3aed' }}>Total: </strong>
                <span>{whatsappMessages.length} messages</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white' }}>
              <h2 style={{ margin: 0 }}>💳 Record Payment</h2>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleRecordPayment}>
              <div className="modal-body">
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <label className="form-label">Amount (₹) *</label>
                    <input type="number" className="form-input" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} required placeholder="e.g., 500000" style={{ fontSize: '18px', fontWeight: '600' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="form-label">Payment Date *</label>
                      <input type="date" className="form-input" value={paymentForm.payment_date} onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} required />
                    </div>
                    <div>
                      <label className="form-label">Payment Mode</label>
                      <select className="form-select" value={paymentForm.payment_mode} onChange={e => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })}>
                        <option>NEFT</option><option>RTGS</option><option>UPI</option><option>Cheque</option><option>Cash</option><option>DD</option><option>Online Transfer</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="form-label">Bank Name</label>
                      <select className="form-select" value={paymentForm.bank_name} onChange={e => setPaymentForm({ ...paymentForm, bank_name: e.target.value })}>
                        <option>HDFC Bank</option><option>ICICI Bank</option><option>SBI</option><option>Axis Bank</option><option>Kotak</option><option>Yes Bank</option><option>PNB</option><option>Bank of Baroda</option><option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Reference/UTR Number</label>
                      <input type="text" className="form-input" value={paymentForm.reference_number} onChange={e => setPaymentForm({ ...paymentForm, reference_number: e.target.value })} placeholder="UTR/Cheque No." />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Description</label>
                    <select className="form-select" value={paymentForm.description} onChange={e => setPaymentForm({ ...paymentForm, description: e.target.value })}>
                      <option>Token Amount</option><option>Booking Amount</option><option>Installment 1</option><option>Installment 2</option><option>Installment 3</option><option>Stamp Duty</option><option>Registration Fee</option><option>Possession Charges</option><option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Notes (Optional)</label>
                    <textarea className="form-input" value={paymentForm.notes} onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })} rows="2" placeholder="Any additional notes..." />
                  </div>
                  <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #86efac' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={paymentForm.send_whatsapp} onChange={e => setPaymentForm({ ...paymentForm, send_whatsapp: e.target.checked })} style={{ width: '18px', height: '18px' }} />
                      <span>📱 Send WhatsApp Receipt to Client</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>Record Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetails;


